import { useState, useEffect, useRef } from 'react';
import { Modal, toast } from '@/components';
import { api } from '@/api/client';
import { useAuthStore } from '@/stores/auth';
import styles from './PdfGenerateModal.module.css';

interface Template {
  id: string;
  name: string;
  is_default: boolean;
}

interface PdfGenerateModalProps {
  open: boolean;
  onClose: () => void;
  templateType: string;
  propertyId?: string;
  customerId?: string;
  contractId?: string;
}

const typeLabel: Record<string, string> = {
  lease: '賃貸借契約書',
  important_explanation: '重要事項説明書',
  sales: '売買契約書',
  management: '管理委託契約書',
};

export function PdfGenerateModal({ open, onClose, templateType, propertyId, customerId, contractId }: PdfGenerateModalProps) {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [step, setStep] = useState<'select' | 'preview'>('select');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedId('');
    setTemplates([]);
    setStep('select');
    if (iframeRef.current) iframeRef.current.srcdoc = '';
    api<Template[]>(`/tenant/templates?type=${templateType}`)
      .then((tpls) => {
        setTemplates(tpls);
        const defaultTpl = tpls.find(t => t.is_default) || tpls[0];
        if (defaultTpl) setSelectedId(defaultTpl.id);
      })
      .catch(() => toast('テンプレート一覧の取得に失敗しました', 'error'))
      .finally(() => setLoading(false));
  }, [open, templateType]);

  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (propertyId) params.set('property_id', propertyId);
    if (customerId) params.set('customer_id', customerId);
    if (contractId) params.set('contract_id', contractId);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  };

  const buildHeaders = () => {
    const { token, user } = useAuthStore.getState();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    if (user?.tenantSlug) headers['X-Tenant-Slug'] = user.tenantSlug;
    return headers;
  };

  const handlePreview = async () => {
    if (!selectedId) return;
    setPreviewing(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const qs = buildQueryString();
      const res = await fetch(`${base}/tenant/templates/${selectedId}/render${qs}`, { headers: buildHeaders() });
      if (!res.ok) throw new Error('render failed');
      const html = await res.text();
      if (iframeRef.current) iframeRef.current.srcdoc = html;
    } catch {
      toast('プレビューの取得に失敗しました', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePrint = () => {
    if (!iframeRef.current) return;
    try {
      // iframe内の印刷バーを非表示にしてから印刷
      const iframeDoc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
      if (iframeDoc) {
        const printBar = iframeDoc.querySelector('.print-bar');
        const printSpacer = iframeDoc.querySelector('.print-spacer');
        if (printBar) (printBar as HTMLElement).style.display = 'none';
        if (printSpacer) (printSpacer as HTMLElement).style.display = 'none';
      }
      iframeRef.current.contentWindow?.print();
    } catch {
      // クロスオリジン制限の場合、新しいウィンドウで開いて印刷
      const html = iframeRef.current.srcdoc;
      if (html) {
        const w = window.open('', '_blank');
        if (w) { w.document.write(html); w.document.close(); }
      }
    }
  };

  const handleDownloadPdf = async () => {
    if (!selectedId) return;
    setDownloading(true);
    try {
      const base = import.meta.env.VITE_API_URL || '/api';
      const qs = buildQueryString();
      const res = await fetch(`${base}/tenant/templates/${selectedId}/pdf${qs}`, { headers: buildHeaders() });
      if (!res.ok) { toast('PDF生成に失敗しました', 'error'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const tpl = templates.find(t => t.id === selectedId);
      const filename = tpl?.name || typeLabel[templateType] || 'document';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${filename}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast('PDFをダウンロードしました', 'success');
    } catch {
      toast('PDF生成に失敗しました', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const selectedTemplate = templates.find(t => t.id === selectedId);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${typeLabel[templateType] || 'PDF'} - テンプレート選択`}
      width={720}
      footer={
        <div className={styles.actions}>
          <button className={styles.btnOutline} onClick={onClose}>閉じる</button>
          <button className={styles.btnOutline} onClick={handlePreview} disabled={!selectedId || previewing}>
            {previewing ? '読み込み中...' : 'プレビュー'}
          </button>
          <button className={styles.btnPrimary} onClick={handlePrint} disabled={!selectedId}>
            🖨 印刷 / PDF保存
          </button>
        </div>
      }
    >
      {loading ? (
        <div className={styles.loading}>読み込み中...</div>
      ) : templates.length === 0 ? (
        <div className={styles.empty}>利用可能なテンプレートがありません</div>
      ) : (
        <>
          <div className={styles.templateList}>
            {templates.map(tpl => (
              <label
                key={tpl.id}
                className={`${styles.templateItem}${selectedId === tpl.id ? ` ${styles.selected}` : ''}`}
              >
                <input
                  type="radio"
                  name="template"
                  checked={selectedId === tpl.id}
                  onChange={() => setSelectedId(tpl.id)}
                />
                <span className={styles.templateName}>{tpl.name}</span>
                {tpl.is_default && <span className={styles.templateMeta}>デフォルト</span>}
              </label>
            ))}
          </div>
          <iframe
            ref={iframeRef}
            className={styles.previewFrame}
            title="テンプレートプレビュー"
            sandbox="allow-same-origin allow-scripts allow-modals"
          />
        </>
      )}
    </Modal>
  );
}
